import { can, signersText } from '../../lib/api'
import type { ExitChain } from '../../lib/exit'
import { Dialog } from '../kit'

// Why this vendor's exit is signed by these offices, in full.
//
// Everything here is the API's own words: the facts that chose the chain, the
// row's reason, each stage's reason with its regulatory basis, and who is told
// when the exit closes. On the workspace each stage keeps one line and its
// reason sits behind the (i); this is where the whole of it is read.

export function AboutStageDrawer({ chain, open, onClose }: { chain: ExitChain; open: boolean; onClose: () => void }) {
  const rule =
    chain.ruleSource === 'shipped default'
      ? 'The shipped offboarding chain applies, because no approval matrix version in force has offboarding rows yet.'
      : 'Approval matrix version ' + (chain.matrixVersionNo ?? '') + '.'
  return (
    <Dialog shape="drawer" kind="record" open={open} onClose={onClose} title="About this sign-off chain">
      <div className="exit-drawer-stack">
        <div>
          <h3 className="exit-h3">
            Why this chain: {chain.facts.tierLabel}
            {chain.facts.personalData ? ', personal data' : ''}
            {chain.rowNoAlternate ? ', no alternate provider' : ''}
          </h3>
          <p className="exit-para">{chain.facts.tierBasis}</p>
          <p className="exit-para">{chain.facts.personalDataBasis}</p>
          <p className="exit-para">{chain.facts.alternateBasis}</p>
          <p className="exit-para">{chain.rowWhy}</p>
        </div>

        <div>
          <h3 className="exit-h3">The stages</h3>
          <ol className="exit-about-stages">
            {chain.stages.map((s) => (
              <li key={s.key}>
                <b>{s.job}</b>
                <span className="exit-sub">Signed by {signersText(s.roles, s.roleNames)}</span>
                <p className="exit-para">{s.why}</p>
                {s.superseded && <p className="exit-para exit-warn">{s.superseded}</p>}
              </li>
            ))}
          </ol>
          <p className="exit-sub">Each stage needs a different person, in order, and not whoever started offboarding</p>
        </div>

        {chain.notify.length > 0 && (
          <div>
            <h3 className="exit-h3">Told when it closes</h3>
            <p className="exit-para">
              {chain.notify.map((n) => n.name).join(' and ')}, by an in-app notice.
            </p>
          </div>
        )}

        {(chain.other ?? []).length > 0 && (
          <div>
            <h3 className="exit-h3">Decisions the chain no longer asks for</h3>
            <ul className="exit-bullets">
              {(chain.other ?? []).map((o) => (
                <li key={o.id}>
                  {o.job}: {o.decision.toLowerCase()} by {o.signedBy}
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className="exit-sub">
          {rule} {can('config.read') && <a href="#/approval-matrix">Read the matrix</a>}
        </p>
      </div>
    </Dialog>
  )
}
